import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince, relativeTime } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { getMe } from './me';
import { type Paginated, delay, paginate } from './mock';

export type NotificationRead = 'all' | 'unread' | 'read';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  date: string;
  updatedHours: number;
  actorId?: string | null;
  actorName?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  targetSlug?: string | null;
  targetLabel?: string | null;
}

/** Stored shape (read state is tracked per member, not on the row). */
type StoredNotification = Omit<AppNotification, 'read'>;

export interface ListNotificationsParams {
  read?: NotificationRead;
  actorId?: string;
  page?: number;
  pageSize?: number;
}

export type PaginatedNotifications = Paginated<AppNotification>;

export const NOTIFICATION_PAGE_SIZE = 20;
export const NOTIFICATION_PAGE_SIZE_OPTIONS = [20, 50, 100];

let db: StoredNotification[] = [
  {
    id: 'n1',
    type: 'subscribers.created',
    title: 'New subscriber',
    body: 'Ana Souza <ana.souza@example.com> joined the list (jury)',
    date: 'Just now',
    updatedHours: 0,
  },
  {
    id: 'n2',
    type: 'emails.captured',
    title: 'Voter email captured',
    body: '<carla.dias@example.com> after voting on Bloom',
    date: '2h ago',
    updatedHours: 2,
  },
  {
    id: 'n3',
    type: 'projects.finalized',
    title: 'Project finalized',
    body: 'Paper Cities: winner Nadia Rehman',
    date: '5h ago',
    updatedHours: 5,
  },
  {
    id: 'n4',
    type: 'users.registration',
    title: 'New user registration',
    body: 'New user registered: marc@example.com via google',
    date: '1d ago',
    updatedHours: 24,
  },
  {
    id: 'n5',
    type: 'system.error',
    title: 'System error',
    body: 'Error occurred: upstream request timed out',
    date: '2d ago',
    updatedHours: 48,
  },
  {
    id: 'n6',
    type: 'activity.artist.created',
    title: 'Artist created',
    body: 'Ana Souza created artist Mara Oliveira',
    date: '3h ago',
    updatedHours: 3,
    actorId: 'ana',
    actorName: 'Ana Souza',
    targetType: 'artist',
    targetSlug: 'mara-oliveira',
    targetLabel: 'Mara Oliveira',
  },
  {
    id: 'n7',
    type: 'activity.project.updated',
    title: 'Project updated',
    body: 'Marc Reid updated project Bloom',
    date: '6h ago',
    updatedHours: 6,
    actorId: 'marc',
    actorName: 'Marc Reid',
    targetType: 'project',
    targetSlug: 'bloom',
    targetLabel: 'Bloom',
  },
  {
    id: 'n8',
    type: 'activity.proposal.updated',
    title: 'Proposal updated',
    body: 'Ana Souza updated proposal Still Water',
    date: '1d ago',
    updatedHours: 24,
    actorId: 'ana',
    actorName: 'Ana Souza',
    targetType: 'proposal',
    targetSlug: 'still-water',
    targetLabel: 'Still Water',
  },
  {
    id: 'n9',
    type: 'activity.member.added',
    title: 'Member added',
    body: 'Ana Souza added member Marc Reid',
    date: '2d ago',
    updatedHours: 48,
    actorId: 'ana',
    actorName: 'Ana Souza',
    targetType: 'member',
    targetId: 'marc',
    targetLabel: 'Marc Reid',
  },
  {
    id: 'n10',
    type: 'activity.voting_period.opened',
    title: 'Voting period opened',
    body: 'Marc Reid opened voting period Bloom',
    date: '8h ago',
    updatedHours: 8,
    actorId: 'marc',
    actorName: 'Marc Reid',
    targetType: 'voting_period',
    targetSlug: 'bloom',
    targetLabel: 'Bloom',
  },
];

/** Read notification ids, per member. */
const readsByMember: Record<string, Set<string>> = {
  ana: new Set(['n4', 'n5', 'n9']),
};

function readsFor(memberId: string): Set<string> {
  readsByMember[memberId] ??= new Set();
  return readsByMember[memberId];
}

/** Wire shape from `GET /admin/notifications`. */
interface NotificationDto {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
  actorId?: string | null;
  actorName?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  targetSlug?: string | null;
  targetLabel?: string | null;
}

function mapNotification(dto: NotificationDto): AppNotification {
  return {
    id: dto.id,
    type: dto.type,
    title: dto.title,
    body: dto.body,
    read: dto.read,
    date: relativeTime(dto.createdAt),
    updatedHours: hoursSince(dto.createdAt),
    actorId: dto.actorId ?? null,
    actorName: dto.actorName ?? null,
    targetType: dto.targetType ?? null,
    targetId: dto.targetId ?? null,
    targetSlug: dto.targetSlug ?? null,
    targetLabel: dto.targetLabel ?? null,
  };
}

export async function listNotifications(
  params: ListNotificationsParams = {},
): Promise<PaginatedNotifications> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.notifications.get({
      query: {
        read: params.read,
        actorId: params.actorId,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    const page = unwrap<Paginated<NotificationDto>>(response);
    return { ...page, items: page.items.map(mapNotification) };
  }

  await delay();
  const { read = 'all', actorId, page, pageSize = NOTIFICATION_PAGE_SIZE } = params;
  const me = await getMe();
  const readSet = readsFor(me.id);

  const filtered = db
    .filter((item) => !actorId || item.actorId === actorId)
    .map((item) => ({ ...item, read: readSet.has(item.id) }))
    .filter((item) => {
      if (read === 'unread') return !item.read;
      if (read === 'read') return item.read;
      return true;
    });

  return paginate(filtered, page, pageSize);
}

export function queryNotifications(
  params: ListNotificationsParams = {},
): Promise<PaginatedNotifications> {
  return listNotifications(params);
}

export async function markNotificationRead(id: string): Promise<void> {
  if (!USE_MOCK) {
    await sdk.api.admin.notifications({ id }).read.patch();
    return;
  }
  await delay();
  const me = await getMe();
  readsFor(me.id).add(id);
}

export async function markAllNotificationsRead(): Promise<void> {
  if (!USE_MOCK) {
    await sdk.api.admin.notifications['read-all'].post();
    return;
  }
  await delay();
  const me = await getMe();
  const readSet = readsFor(me.id);
  for (const item of db) readSet.add(item.id);
}

export interface ActivityInput {
  verb: string;
  entity: string;
  targetType?: string;
  targetId?: string;
  targetSlug?: string | null;
  targetLabel?: string | null;
}

/** Appends an activity-log entry for the current member (mock only). */
export async function pushActivity(input: ActivityInput): Promise<void> {
  // The API records activity server-side on each mutation.
  if (!USE_MOCK) return;

  const me = await getMe();
  const actorName = me.name ?? me.email;
  const entityLabel = input.entity.charAt(0).toUpperCase() + input.entity.slice(1);
  const label = input.targetLabel ? ` ${input.targetLabel}` : '';

  db = [
    {
      id: crypto.randomUUID(),
      type: `activity.${input.entity.replace(/\s+/g, '_')}.${input.verb}`,
      title: `${entityLabel} ${input.verb}`,
      body: `${actorName} ${input.verb} ${input.entity}${label}`,
      date: 'Just now',
      updatedHours: 0,
      actorId: me.id,
      actorName,
      targetType: input.targetType ?? null,
      targetId: input.targetId ?? null,
      targetSlug: input.targetSlug ?? null,
      targetLabel: input.targetLabel ?? null,
    },
    ...db,
  ];
}
